import React, { useEffect, useRef, useState } from 'react';
import { Select, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { TEMU_CARRIER_DHLE, TEMU_CARRIER_USPS } from '../types/ChuteEnums';
import type { TemuSortingCodeOptions } from '../hooks/useTemuSortingCodeOptions';

/** 第一段：类型；第二段：危险品标识。与 PC 建 Temu 包牌表单同一套硬编码取值 */
const TEMU_TYPE_OPTIONS = ['GA', 'SCF'] as const;
const TEMU_HAZMAT_OPTIONS = ['G', 'H'] as const;

type TemuType = (typeof TEMU_TYPE_OPTIONS)[number];

interface TemuSegments {
    type: string | undefined;
    hazmat: string | undefined;
    destination: string | undefined;
    largeBox: string | undefined;
}

/** DHLe 只有一段且就是目的地，不能按 USPS 的「类型-危险品-目的地-大箱码」位置去拆 */
const parseSegments = (value: string | null | undefined, isDhle: boolean): TemuSegments => {
    const segs = (value || '').split('-').map(s => s.trim()).filter(Boolean);
    if (isDhle) {
        return { type: undefined, hazmat: undefined, destination: segs[0], largeBox: undefined };
    }
    return {
        type: segs[0],
        hazmat: segs[1],
        destination: segs[2],
        largeBox: segs[3],
    };
};

interface TemuSortingCodeSelectProps {
    value: string | null | undefined;
    carrierCode: number | null | undefined;
    disabled: boolean;
    options: TemuSortingCodeOptions;
    /** 只在凑齐完整分拣规则（USPS：GA 三段 / SCF 四段；DHLe：一段）或清空时回调；中间态留在组件内 */
    onChange: (value: string | null) => void;
}

/**
 * TEMU 格口分拣规则分段选择：
 * - USPS：类型（GA/SCF）→ 危险品（G/H）→ 目的地（随类型变）→ 大箱码（仅 SCF）
 * - DHLe：单段目的地
 * - 未选尾程商或尾程商不是 USPS/DHLe：置灰
 */
const TemuSortingCodeSelect: React.FC<TemuSortingCodeSelectProps> = ({ value, carrierCode, disabled, options, onChange }) => {
    const { t } = useTranslation();
    const isUsps = carrierCode === TEMU_CARRIER_USPS;
    const isDhle = carrierCode === TEMU_CARRIER_DHLE;
    const [segments, setSegments] = useState<TemuSegments>(() => parseSegments(value, isDhle));
    // 标记「这次 value 变成 null 是组件自己因中间态清掉的」：此时不能重置本地段，否则刚选的类型/危险品会被一起清空
    const selfClearedRef = useRef(false);

    // 外部值变化（如切尾程商被清空、保存后回填）时同步本地中间态
    useEffect(() => {
        if (value == null && selfClearedRef.current) {
            selfClearedRef.current = false;
            return;
        }
        setSegments(parseSegments(value, isDhle));
    }, [value, isDhle]);

    const selectDisabled = disabled || (!isUsps && !isDhle);

    const emitIfComplete = (next: TemuSegments) => {
        setSegments(next);
        if (isDhle) {
            onChange(next.destination || null);
            return;
        }
        const { type, hazmat, destination, largeBox } = next;
        if (type && hazmat && destination && (type !== 'SCF' || largeBox)) {
            onChange(type === 'SCF' ? `${type}-${hazmat}-${destination}-${largeBox}` : `${type}-${hazmat}-${destination}`);
        } else if (value) {
            // 之前是完整值、现在被改成中间态：清掉已存的完整值，避免保存时提交旧规则；本地段保留
            selfClearedRef.current = true;
            onChange(null);
        }
    };

    const toOptions = (codes: readonly string[]) => codes.map(code => ({ value: code, label: code }));

    if (isDhle) {
        return (
            <Select
                size="small"
                style={{ width: '100%' }}
                allowClear
                showSearch
                disabled={selectDisabled}
                loading={options.loading}
                placeholder={t('wms.sorterPlan.detail.placeholders.temuDestination') as string}
                value={segments.destination}
                options={toOptions(options.dhleDestinations)}
                onChange={(destination?: string) => emitIfComplete({ ...segments, destination })}
            />
        );
    }

    const type = segments.type as TemuType | undefined;
    const segmentWidth = type === 'SCF' ? '25%' : '33.34%';
    const destinations = type === 'GA' ? options.gaDestinations : type === 'SCF' ? options.scfDestinations : [];

    return (
        <Space.Compact style={{ width: '100%' }}>
            <Select
                size="small"
                // 与其它行的输入框同宽（撑满整列），段数不同就等分：GA 三段各 1/3，SCF 四段各 1/4；
                // 四段并排每格只有 60px 左右，全部去掉下拉箭头，不然占位文字「目的地 / 大箱码」被截成「目…」
                style={{ width: segmentWidth }}
                suffixIcon={null}
                disabled={selectDisabled}
                placeholder={t('wms.sorterPlan.detail.placeholders.temuType') as string}
                value={type}
                options={toOptions(TEMU_TYPE_OPTIONS)}
                // 类型变了目的地候选随之变、大箱码是否启用也变，两者一并清空
                onChange={(nextType?: string) => emitIfComplete({ type: nextType, hazmat: segments.hazmat, destination: undefined, largeBox: undefined })}
            />
            <Select
                size="small"
                style={{ width: segmentWidth }}
                suffixIcon={null}
                disabled={selectDisabled}
                placeholder={t('wms.sorterPlan.detail.placeholders.temuHazmat') as string}
                value={segments.hazmat}
                options={toOptions(TEMU_HAZMAT_OPTIONS)}
                onChange={(hazmat?: string) => emitIfComplete({ ...segments, hazmat })}
            />
            <Select
                size="small"
                style={{ width: segmentWidth }}
                showSearch
                suffixIcon={null}
                disabled={selectDisabled || !type}
                loading={options.loading}
                placeholder={t('wms.sorterPlan.detail.placeholders.temuDestination') as string}
                value={segments.destination}
                options={toOptions(destinations)}
                onChange={(destination?: string) => emitIfComplete({ ...segments, destination })}
            />
            {type === 'SCF' && (
                <Select
                    size="small"
                    style={{ width: segmentWidth }}
                    suffixIcon={null}
                    disabled={selectDisabled}
                    loading={options.loading}
                    placeholder={t('wms.sorterPlan.detail.placeholders.temuLargeBox') as string}
                    value={segments.largeBox}
                    options={toOptions(options.largeBoxCodes)}
                    onChange={(largeBox?: string) => emitIfComplete({ ...segments, largeBox })}
                />
            )}
        </Space.Compact>
    );
};

export default TemuSortingCodeSelect;
